/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useState, useContext, useRef } from 'react';
import { inspect } from 'util';
import moment from 'moment';
import axios from 'axios';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useSession } from '@marriott/mi-headless-utils';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Types, Button, Text, InputTextField, Heading, RichText, Messages } from '@marriott/mi-ui-library';
import { getUserDetails, useClientEnvVarsStore, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';

import {
  checkChinaLocale,
  getCountryAndLangCode,
  CHINESE_MOBILE_CODE,
  logger,
  handleRedirect,
  clearSessionAndCookie,
  calculateOtpTimeLeft,
} from '../../modules/utils';
import { updatePublicKey, updateUserLoginDataLayer } from '../../modules/utils/userLogin.helper';
import {
  constants,
  BANNER_MSGS_KEYS,
  PROFILE_REDIRECTION_URL,
  MFA_OPTIONS_KEY,
  generateOneTimePwrdUrl,
  API_ERROR_KEY,
  FORGOT_PWRD_ID,
  TRACKING_PAGE_NAME,
  NON_SUPPORTED_PHONE_NUMBER,
  DUPLICATE_PHONE_ERROR_KEY,
  ACTIVATE_SUCCESS_SIGNIN_FAILED_ERROR_KEY,
  INVALID_EMAIL_ADDRESS,
  INVALID_EMAIL_INLINE_ERROR,
  flyoutModalKeys,
  CHANGE_PASSWORD_TOKEN_EXPIRED_ERROR_KEY,
  PHOENIX_SEND_OTP_CHALLENGE_URL,
  FLYOUT_CONFIG_VALUES,
  TRACKING_CONSTANTS,
  FORGOT_PASSWORD_KEY,
  CN_FLYOUT_STEPS,
  CN_CONTACT_MODES,
} from '../../modules/utils/constants/constants';
import { useAccountPersistentStore } from '../../modules/store/accountPersistentStore';
import { useBannerMessagesStore } from '../../modules/store/bannerMessagesStore';
import { useAccountModelStore } from '../../modules/store/accountModelStore';
import { useStore } from '../../modules/store/memberLevelStore';
import {
  getCurrentUrlParams,
  encryptPassword,
  getRedirectUrlFromParams,
  checkMobileNumValidity,
  isCoutriesSame,
  addSubDirectoryPrefixForNonEaa,
  scrollToClass,
  canUseDOM,
  getCustomClickTrackingPageName,
  getSessionDataWithFallback,
  resolveCnFlyoutContactMode,
  getMaskedCnPhoneDisplay,
  isValidEmaiFormat,
} from '../../modules/utils/helper';
import { addSubDirectoryPrefix, useCheckBreakpoint } from '../../modules';
import { submitCreateOnlineAccount, newPwrdAPI } from '../../modules/utils/constants/constants';
import { fetchLinkJson, useFlyoutNavigation } from '../../modules/utils/flyoutHelper';
import { PageContext } from '../../modules/context/PageContext';
import { useAccountContainerContext } from '../../modules/context/AccountContainerContext';
import { PhoneDetails } from '../ActivateOrForgotForm/ActivateOrForgotForm.types';
import { StyledInputTextFieldNumber } from '../PasswordInput/PasswordInput.styles';
import { PasswordInput } from '../PasswordInput';
import { PhoneNumberField } from '../PhoneNumberField';
import { OtpTimer } from '../OtpTimer';
import { SetPasswordProps, FormData, FormError, ApiError } from './SetPassword.types';
import { FlyOutModalStyle, StyledSetPassword } from './SetPassword.styles';

const initialFormData: FormData = {
  email: '',
  password: '',
  confirmPwrd: '',
  mobilePhone: '',
  verificationCode: '',
};

const { log } = logger({})('SetPassword');
const changePasswordInitalFormData: FormData = {
  password: '',
  confirmPwrd: '',
};

// Use named rather than default exports.
export const SetPassword: FC<SetPasswordProps> = ({
  isFormDisabled,
  authorModelData,
  userDetails,
  setPageError,
  setTypeTwoErrorHeader,
  setDisplayTypeOneError,
  phoneDetails,
  trackingPageName,
  isChangePwrd, //use this for showing error on resettoken expiry
  inlineLabel = true, // use this to change input to older version
  enablePhaseOne,
  enablePhaseThree,
  uxlFailureBannerText,
  isFlyout,
  accountEvent,
  stepCounter,
}) => {
  const pageModuleName = trackingPageName || authorModelData?.trackingPageName || '';
  const [transactionId, setTransactionId] = useState<PhoneDetails>();
  const [encryptedPhone, setEncryptedPhone] = useState<string>('');
  const passwordCondArr = [
    {
      condition: authorModelData?.pwrdCharCheck,
      isValid: false,
    },
    {
      condition: authorModelData?.pwrdUpperCaseCheck,
      isValid: false,
    },
    {
      condition: authorModelData?.pwrdLowerCaseCheck,
      isValid: false,
    },
    {
      condition: authorModelData?.pwrdSpecialCharCheck,
      isValid: false,
    },
  ];
  const passwordCondLabelArr = [
    {
      condition: authorModelData?.pwrdCharLabel,
      isValid: false,
    },
    {
      condition: authorModelData?.pwrdUpperCaseLabel,
      isValid: false,
    },
    {
      condition: authorModelData?.pwrdLowerCaseLabel,
      isValid: false,
    },
    {
      condition: authorModelData?.pwrdSpecialCharLabel,
      isValid: false,
    },
  ];
  const { isACDLenabled } = useSession();
  const clientEnvVars = useClientEnvVarsStore(state => state.envVarsObject);
  const { HOMEPAGE_REDIRECT_URL: homePageRedirectUrl } = clientEnvVars;
  const pageContext = useContext(PageContext);
  const sessionData = getSessionDataWithFallback(pageContext?.sessionData)?.cacheData?.data;
  const isEAA = pageContext?.isEAASignIn;
  const { currentLocale: contextCurrentLocale } = useAccountContainerContext();
  const {
    isMobileAuthFeatureEnabled,
    isNonChinaMobileEnabled,
    currentLocale: pageContextLocale,
    featureFlags,
  } = pageContext;
  const currentLocale = pageContextLocale || contextCurrentLocale || 'en_US';
  const cnActivationLocale = clientEnvVars?.['ACCEPT_LANGUAGE'] || currentLocale || '';
  const isCnActivationFlyout =
    !!isFlyout && !!enablePhaseOne && !isChangePwrd && checkChinaLocale(cnActivationLocale);
  const isCNEaaPayloadEncrypt = isEAA && featureFlags?.[FEATURE_FLAGS.CNEXPERIENCE_CNMCOM_EAA_PAYLOAD_ENCRYPT];
  const emailInputRef = useRef<any>(null);
  const phoneInputRef = useRef<HTMLDivElement | null>(null);
  const otpInputRef = useRef<HTMLDivElement | null>(null);
  const [passwordType, setPasswordType] = useState<'text' | 'password'>('password');
  const [confirmPasswordType, setConfirmPasswordType] = useState<'text' | 'password'>('password');
  const [errors, setErrors] = useState<FormError>({});
  const [errorsList, setErrorsList] = useState<string[]>([]);
  const [displayTypeOneErrorInside, setDisplayTypeOneErrorInside] = useState(false);
  const [formData, setFormData] = useState<FormData>(isChangePwrd ? changePasswordInitalFormData : initialFormData);
  const [passwordConditions, setPasswordConditions] = useState(
    authorModelData?.pwrdHeader ? passwordCondLabelArr : passwordCondArr,
  );
  const [isLoading, setIsloading] = useState<boolean>(false);
  const [passwordValid, setPasswordValid] = useState(false);
  const isDesktopAndAbove = useCheckBreakpoint('viewportL');
  let pageLevelErrors: string[] = [];
  const { countryCode } = getCountryAndLangCode(isCnActivationFlyout ? cnActivationLocale : currentLocale);
  const router = useRouter();
  const { resetToken, mobileEditable, validateToken } = router.query;
  const isMobileEditable = mobileEditable === 'true' || userDetails?.mobileEditable;
  const countryDialCode = authorModelData?.countriesDialCodes?.find(
    (d: any) => d.id?.toUpperCase() === countryCode?.toUpperCase(),
  )?.value;
  const defaultContryCode = authorModelData?.countriesDialCodes?.find(
    (d: any) => d.id?.toUpperCase() === countryCode?.toUpperCase(),
  )?.id;
  const [selectedCountryCode, setSelectedCountryCode] = useState(countryDialCode);
  const [timeLeft, setTimeLeft] = useState(0);
  const otpMapRef = useRef<{ [phone: string]: number }>({});
  const typeOneErrorKeys = [
    'fcpEnabledErrorMessage',
    'pwrdSubmissionFail',
    'signApiFailure',
    'inActiveUserErrorMsg',
    'expiredOtpWarningMessage',
    API_ERROR_KEY,
  ];
  const { setBannerMsgs, setBannerId, setInterPageErrorMsg } = useBannerMessagesStore();
  const {
    setTokenExpiredFlyout,
    setActiveKey,
    modelData,
    setModelData,
    setModelKey,
    setIsContentLoading,
    validateToken: storeValidateToken,
    setValidateToken,
  } = useAccountModelStore(state => state);
  const { cnFlyoutStep, setCnFlyoutStep } = useAccountModelStore(state => ({
    cnFlyoutStep: state.cnFlyoutStep,
    setCnFlyoutStep: state.setCnFlyoutStep,
  }));

  const isCNFlyout = isCnActivationFlyout && cnFlyoutStep != null;
  const isCNContactStep = isCNFlyout && cnFlyoutStep === CN_FLYOUT_STEPS.CONTACT_INFO;
  const isCNPasswordStep = isCNFlyout && cnFlyoutStep === CN_FLYOUT_STEPS.SET_PASSWORD;
  const cnContactMode = resolveCnFlyoutContactMode(phoneDetails, userDetails?.country);
  const cnProfilePhone =
    phoneDetails?.phoneNumber && !phoneDetails?.phoneNumber?.includes(NON_SUPPORTED_PHONE_NUMBER)
      ? phoneDetails.phoneNumber
      : '';
  const enteredCnMobile = formData?.mobilePhone?.replace(selectedCountryCode || '', '').trim() || '';
  const cnRequiresOtp =
    isCNFlyout &&
    (cnContactMode === CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP ||
      (cnContactMode === CN_CONTACT_MODES.COLLECT_CN_PHONE_OTP &&
        ((!!enteredCnMobile &&
          selectedCountryCode === CHINESE_MOBILE_CODE &&
          checkMobileNumValidity(formData?.mobilePhone)) ||
          (isCNPasswordStep && !!formData.mobilePhone?.trim()))));
  const cnStoredEmailDisabled =
    isCNContactStep &&
    cnContactMode !== CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP &&
    (isEAA || !checkChinaLocale(cnActivationLocale)
      ? !!phoneDetails?.emailAddress
      : !phoneDetails?.maskedNumber && !phoneDetails?.isChinaMobileNumber && !!phoneDetails?.emailAddress?.trim());
  const cnOtpPhone =
    cnContactMode === CN_CONTACT_MODES.COLLECT_CN_PHONE_OTP
      ? formData.mobilePhone || cnProfilePhone
      : cnProfilePhone || formData.mobilePhone;
  const [publicKey, setPublicKey] = useState('');
  const {
    communicationOption,
    setCommunicationOption,
    setMemberDetailsUxl,
    setIsEAAEnrollFailed,
    setShowBrowserRegistrationCta,
    maskedPhone,
    maskedEmail,
  } = useAccountPersistentStore(state => state);
  const [showOTPSpinner, setShowOTPSpinner] = useState(false);
  const { rememberedUser } = useUserDetailsGlobalStore(state => state);
  const { setShowCustomMessage } = useStore(state => state);
  const { SET_PASSWORD_SUBMIT, CHANGE_PASSWORD_SUBMIT, INTERNAL, ACTIVATE_ACCOUNT_RHS_SUBMIT } = TRACKING_CONSTANTS;
  const customClickTrackingPageName = getCustomClickTrackingPageName(accountEvent?.trackingPageName, router?.pathname);
  const submitCustomClickTrackValue = `${customClickTrackingPageName}|${
    isChangePwrd ? CHANGE_PASSWORD_SUBMIT : SET_PASSWORD_SUBMIT
  }|${INTERNAL}|`;

  const [isOtpSent, setIsOtpSent] = useState(false);
  const [showSendCode, setShowSendCode] = useState(true);
  const [cancelButtonLink, setCancelButtonLink] = useState<string>('');
  const selectedCountryCodeId = authorModelData?.countriesDialCodes?.find(
    (d: any) => d.value === selectedCountryCode,
  )?.id;

  const resetForm = (fieldName: string) => {
    const newErrors: any = { ...errors };

    switch (fieldName) {
      case 'password':
        newErrors.newPwrdRequired = '';
        break;
      case 'confirmPwrd':
        newErrors.confirmPwrdAndPwrdMatch = '';
        break;
      case 'email':
        newErrors.emailWarning = '';
        newErrors.emailRequired = '';
        newErrors.emailInvalidInlineError = '';
        newErrors.invalidEmailAddress = '';
        break;
      default:
    }
    setErrors(newErrors);
  };
  const handleChange = (name: string, value?: string) => {
    value && resetForm(name);
    if (name === 'password') {
      validatePassword(value || '');
    }
    setFormData(prevData => ({ ...prevData, [name]: value }));
  };
  const matchConfirmPassword = () => {
    let isValid = true;
    const errObj: any = {};
    if (formData.confirmPwrd !== formData.password) {
      errObj.confirmPwrdAndPwrdMatch = authorModelData?.confirmPwrdAndPwrdMatch;
      pageLevelErrors.push(authorModelData?.confirmPwrdMustMatchPwrd);
      isValid = false;
    } else {
      errObj.confirmPwrdAndPwrdMatch = undefined;
      isValid = true;
    }
    setErrors({ ...errors, ...errObj });
    return isValid;
  };
  const checkPasswordAndEmail = () => {
    let isValid = true;
    const errObj: any = {};
    if (!isChangePwrd) {
      if (isCNFlyout) {
        errObj.emailRequired = undefined;
      } else if (!checkChinaLocale(currentLocale || '') && !formData.email?.trim()) {
        errObj.emailRequired = authorModelData?.emailRequired;
        pageLevelErrors.push(authorModelData?.emailRequired);
        isValid = false;
      } else if (checkChinaLocale(currentLocale || '')) {
        if (isChinesePhoneNumberValid()) {
          errObj.emailRequired = undefined;
        } else if (!formData.email?.trim()) {
          errObj.emailRequired = authorModelData?.emailRequired;
          pageLevelErrors.push(authorModelData?.emailRequired);
          isValid = false;
        }
      }
    }
    if (!formData.password?.trim()) {
      errObj.newPwrdRequired = authorModelData?.newPwrdRequired;
      pageLevelErrors.push(authorModelData?.newPwrdRequired);
      isValid = false;
    } else {
      errObj.newPwrdRequired = undefined;
    }
    if (
      !isCNFlyout &&
      ((!isEAA && !isChangePwrd && isMobileAuthFeatureEnabled) ||
        ((isEAA || checkChinaLocale(currentLocale || '')) && isChinesePhoneNumberValid())) &&
      !checkMobileNumValidity(formData?.mobilePhone)
    ) {
      errObj.invalidMobilePhone = authorModelData?.invalidMobilePhone;
      pageLevelErrors.push(authorModelData?.invalidMobilePhoneMsg);
      pageLevelErrors.push(authorModelData?.invalidMobilePhone);
      isValid = false;
    } else {
      errObj.mobilePhoneRequired = undefined;
    }
    if (
      (cnRequiresOtp ||
        (!isCNFlyout &&
          (((isEAA || checkChinaLocale(currentLocale || '')) && isChinesePhoneNumberValid())))) &&
      !formData.verificationCode?.trim()
    ) {
      const verificationCodeError = authorModelData?.verificationCodeError;
      errObj.verificationCodeError = verificationCodeError;
      verificationCodeError && pageLevelErrors.push(verificationCodeError);
      isValid = false;
    } else {
      errObj.verificationCodeError = undefined;
    }
    setErrors({ ...errors, ...errObj });
    return isValid;
  };
  const validatePassword = (value: string) => {
    const str = value;
    const passwordArr = [...passwordConditions];
    if (passwordArr[0]) {
      passwordArr[0].isValid = 8 <= str.length && str.length <= 20;
    }
    if (passwordArr[1]) {
      passwordArr[1].isValid = !!str.match(/[A-Z]/g);
    }
    if (passwordArr[2]) {
      passwordArr[2].isValid = !!str.match(/[a-z]/g);
    }
    if (passwordArr[3]) {
      passwordArr[3].isValid = !!str.match(/^(?=.*[0-9$!#&@?%=_])[A-Za-z0-9$!#&@?%=_]{1,}$/g);
    }
    setPasswordConditions([...passwordArr]);
  };

  const getUserRemeberDetails = () => {
    try {
      const sessionToken =
        sessionData?.sessionToken || sessionData?.data?.sessionToken || sessionData?.data?.cacheData?.sessionToken;
      log.debug(`[User Remember] function called sessionID: ${sessionToken}`);
      getUserDetails(false);
    } catch (e) {
      log.debug(`[User Remember] error sessionID: ${sessionData?.sessionToken}, ${inspect(e)}`);
    }
  };

  useEffect(() => {
    updatePublicKey(setPublicKey);
    isChangePwrd && !isEAA && getUserRemeberDetails();
    if (isChangePwrd) {
      const params = new URLSearchParams(window?.location?.search);
      params.delete('resetToken');
      params.delete('validateToken');
      const updatedLink = !isEAA
        ? addSubDirectoryPrefix(
            authorModelData?.cancelCtaPath + (params.toString().length ? '?' + params.toString() : ''),
          )
        : authorModelData?.cancelCtaPath + '?' + params.toString();
      setCancelButtonLink(updatedLink);
    }
    return () => {
      if (isCNFlyout) {
        setCnFlyoutStep(null);
      }
    };
  }, []);

  useEffect(() => {
    if (!isChangePwrd) return;

    const hasMaskedPhoneOtp =
      !!maskedPhone?.length && (checkChinaLocale(currentLocale || '') || validateToken || storeValidateToken);

    if (hasMaskedPhoneOtp) {
      setIsOtpSent(true);
    }
  }, [maskedPhone]);

  // set phone number value when recieved from API
  useEffect(() => {
    if (phoneDetails) {
      setFormData(prevData => ({
        ...prevData,
        mobilePhone: phoneDetails?.phoneNumber?.includes(NON_SUPPORTED_PHONE_NUMBER)
          ? ''
          : (phoneDetails?.phoneNumber ?? ''),
        email: phoneDetails?.emailAddress ?? '',
      }));
    }
  }, [phoneDetails]);

  useEffect(() => {
    if (isCNFlyout) {
      setSelectedCountryCode(CHINESE_MOBILE_CODE);
    }
  }, [isCNFlyout]);

  useEffect(() => {
    const isPasswordValid = passwordConditions.reduce((acc, curr) => acc && curr.isValid, true);
    setPasswordValid(isPasswordValid);
  }, [passwordConditions]);

  useEffect(() => {
    if (!isFormDisabled) {
      //focus on phone number field if it is present, else focu on email field
      if (
        !isChangePwrd &&
        ((isMobileAuthFeatureEnabled && (!userDetails?.givenName || !userDetails?.surName)) ||
          ((isEAA || checkChinaLocale(currentLocale || '')) && isChinesePhoneNumberValid()))
      ) {
        if (!isMobileEditable && !!phoneDetails?.phoneNumber) {
          const otpInput = otpInputRef.current?.querySelector('input');
          if (otpInput) {
            otpInput?.focus();
          }
        } else {
          const phoneInput = phoneInputRef.current?.querySelector('input');
          if (phoneInput) {
            phoneInput?.focus();
          }
        }
      } else {
        emailInputRef?.current?.focus();
      }

      if (hasPhoneNumber && !isCnActivationFlyout && !cnFlyoutStep) {
        setIsOtpSent(true);
        setShowSendCode(false);
        otpMapRef.current[phoneDetails?.phoneNumber || ''] = Date.now();
        setTimeLeft(authorModelData?.timer);
      }
    }
  }, [isFormDisabled, userDetails, phoneDetails?.phoneNumber, isCnActivationFlyout, cnFlyoutStep]);

  const validatePasswordFields = () => {
    pageLevelErrors = [];
    let isValid = true;
    isValid = matchConfirmPassword() && checkPasswordAndEmail();
    if (pageLevelErrors?.length) {
      setDisplayTypeOneError?.(false);
      setTypeTwoErrorHeader?.(authorModelData?.errorMessage);
    }
    const visiblePageLevelErrors = pageLevelErrors.filter(Boolean);
    setPageError?.(visiblePageLevelErrors);
    isChangePwrd && setErrorsList?.(visiblePageLevelErrors);
    return isValid;
  };

  const fetchMobileNumber = (obj: any) => {
    setShowSendCode(true);
    const previousPhone = formData?.mobilePhone || '';
    const nextMobileNumber = obj.mobileNumber?.replace(/\s+/g, '') || '';
    const nextPhone = nextMobileNumber ? `${obj.countryCode}${nextMobileNumber}` : '';
    const hasPhoneChanged = previousPhone !== nextPhone;
    if (isCNContactStep) {
      if (previousPhone && hasPhoneChanged) {
        delete otpMapRef.current[previousPhone];
      }
      if (hasPhoneChanged) {
        setIsOtpSent(false);
        setTimeLeft(0);
        setTransactionId(undefined);
        setEncryptedPhone('');
      }
    }
    setFormData(prevData => ({
      ...prevData,
      mobilePhone: nextPhone,
      ...(isCNContactStep ? { verificationCode: '' } : {}),
    }));
    setSelectedCountryCode(obj?.countryCode);
    if (obj.mobileNumber?.trim()?.length) {
      if (errors.mobilePhoneRequired || errors.phoneAlreadyAssociatedInlineError) {
        setDisplayTypeOneError?.(false);
        setPageError && setPageError([]);
      }
      setErrors({
        ...errors,
        mobilePhoneRequired: undefined,
        mobilePhoneWarning: undefined,
        phoneAlreadyAssociatedInlineError: undefined,
      });
    }
  };

  const { handleFlyoutNavigation } = useFlyoutNavigation();
  const handleClick = async (link: string) => {
    setIsContentLoading(true);
    await handleFlyoutNavigation(link).catch(() => {
      setErrorsList([uxlFailureBannerText || '']);
    });
    setIsContentLoading(false);
  };

  const handleSubmit = async () => {
    setIsloading(true);
    try {
      if (!validatePasswordFields()) {
        scrollToClass();
        return;
      }
      const urlParam = getCurrentUrlParams();
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
      const cnEaaPhoneNumber = encryptedPhone
        ? encryptedPhone
        : isCNEaaPayloadEncrypt && !isMobileEditable && phoneDetails?.phoneNumber
          ? phoneDetails.phoneNumber
          : '';
      if (!isChangePwrd) {
        await axios
          .post(
            addSubDirectoryPrefixForNonEaa(submitCreateOnlineAccount, isEAA),
            {
              givenName: userDetails?.givenName,
              surName: userDetails?.surName,
              emailAddress: formData.email,
              pageModuleName,
              password: formData.password && encryptPassword(formData.password, publicKey),
              confirmPassword: formData.confirmPwrd && encryptPassword(formData.confirmPwrd, publicKey),
              country: userDetails?.country,
              returnUrl: !isEAA ? addSubDirectoryPrefix(enablePhaseOne ? returnUrl : PROFILE_REDIRECTION_URL) : '',
              memberName: userDetails?.memberName,
              rememberMe: '',
              postalCode: userDetails?.postalCode,
              transactionId: transactionId ? transactionId?.transactionId : phoneDetails?.transactionId,
              oneTimePassword: formData?.verificationCode,
              chinaMobileNumber: isCNFlyout ? cnRequiresOtp : phoneDetails?.isChinaMobileNumber,
              cnEaaPayloadEncrypt: isCNEaaPayloadEncrypt,
              ...(isEAA &&
                (userDetails?.prefillQueryParam
                  ? { prefillQuery: userDetails?.prefillQueryParam }
                  : urlParam
                    ? { queryParams: urlParam }
                    : '')),
              ...getCreateOnlineAccountPhonePayload(cnEaaPhoneNumber),
            },
            {
              headers: {
                'Content-Type': 'application/json',
                isEAA: isEAA ?? '',
              },
            },
          )
          .then(data => {
            if (data.status === 200) {
              if (isEAA && !data?.data?.isRedirect) {
                setShowCustomMessage(true);
              } else {
                handleRedirect(addSubDirectoryPrefix(data?.data?.nextStateURI));
              }
            }
          });
      } else {
        // fetch resetToken from session data and pass in payload
        const payload: any = {
          pwrd: formData.password && encryptPassword(formData.password, publicKey),
          confirmPwrd: formData.confirmPwrd && encryptPassword(formData.confirmPwrd, publicKey),
          resetToken: enablePhaseOne ? modelData?.['resetToken'] : resetToken,
          transactionId: transactionId
            ? transactionId?.transactionId
            : typeof validateToken === 'string'
              ? validateToken
              : typeof storeValidateToken === 'string'
                ? encodeURIComponent(storeValidateToken)
                : '',
          oneTimePwrd: formData.verificationCode,
          returnUrl:
            getRedirectUrlFromParams(isEAA, false) || homePageRedirectUrl || FLYOUT_CONFIG_VALUES.HOMEPAGE_REDIRECT_URL,
          errorUrl: '/forgotPassword',
          rememberedUser: rememberedUser,
          communicationOption: communicationOption,
          pageModuleName,
          ...(isEAA && urlParam && { queryParams: urlParam }),
        };

        await axios
          .post(
            addSubDirectoryPrefixForNonEaa(newPwrdAPI, isEAA),
            {
              ...payload,
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            },
          )
          .then(data => {
            if (data?.data?.customerDetails) {
              clearSessionAndCookie();
              setMemberDetailsUxl?.(data?.data?.customerDetails);
            }
            if (data?.status === 200) {
              setValidateToken(null);
              setCommunicationOption?.(''); // setting communicationOption to default when change password api is success
              if (isChangePwrd) {
                const responseData = data?.data?.mfaOption;
                if (
                  responseData &&
                  (responseData?.emailAddress || (responseData?.phoneNumbers && responseData?.phoneNumbers?.length > 0))
                ) {
                  sessionStorage.setItem(TRACKING_PAGE_NAME, pageModuleName);
                  sessionStorage.setItem(MFA_OPTIONS_KEY, JSON.stringify(responseData));
                  setShowBrowserRegistrationCta &&
                    setShowBrowserRegistrationCta(data?.data?.showBrowserRegistrationCta);
                }
              }
              setBannerId(FORGOT_PWRD_ID);
              setBannerMsgs(BANNER_MSGS_KEYS.PWRD_UPDATED_MSG);
              // we need to avoid decoding for mcom and nextStateURI have returnTo query param
              const shouldDecode = isEAA || !data?.data?.nextStateURI?.includes('returnTo');
              if (enablePhaseThree && !isEAA && data?.data?.nextStateURI?.includes(PHOENIX_SEND_OTP_CHALLENGE_URL)) {
                handleClick(data?.data?.nextStateURI);
              } else {
                handleRedirect(addSubDirectoryPrefix(data?.data?.nextStateURI), false, shouldDecode);
              }
            }
          });
      }
    } catch (error: any) {
      if (error) {
        const errorResponse = error?.response?.data;
        if (errorResponse?.isRedirect) {
          const errorMessage = errorResponse?.phoenixErrorMessages?.errorMessages?.[0];
          if (
            isEAA &&
            errorResponse?.nextStateURI?.includes('/login') &&
            errorMessage === ACTIVATE_SUCCESS_SIGNIN_FAILED_ERROR_KEY
          ) {
            setIsEAAEnrollFailed?.(true);
          }
          !isEAA && setInterPageErrorMsg(errorMessage);
          handleRedirect(errorResponse?.nextStateURI);
        } else {
          const errorArray: any = [];
          const errorObject: any = {};

          // for inActiveUserErrorMsg redirect back to forgot password component

          // For M.Com china flow, we have to show expiredOtpWarningMessage error label for otp expired
          const errorBody =
            !isEAA && checkChinaLocale(currentLocale || '')
              ? {
                  errorMessages: errorResponse?.phoenixErrorMessages?.errorMessages?.map((item: string) =>
                    item === (isChangePwrd ? CHANGE_PASSWORD_TOKEN_EXPIRED_ERROR_KEY : 'expiredTransactionCode')
                      ? 'expiredOtpWarningMessage'
                      : item,
                  ),
                }
              : errorResponse?.phoenixErrorMessages;

          errorBody?.errorMessages?.map((key: string) => {
            const errorString =
              key === DUPLICATE_PHONE_ERROR_KEY
                ? getAuthoredApiError(key) || getDuplicatePhonePageError()
                : getAuthoredApiError(key);
            errorString && errorArray.push(errorString);
            errorString && (errorObject[key] = errorString);
            const inlineError = key === INVALID_EMAIL_ADDRESS ? authorModelData[INVALID_EMAIL_INLINE_ERROR] : '';
            inlineError && (errorObject[INVALID_EMAIL_INLINE_ERROR] = inlineError);
            const duplicatePhoneInlineError =
              key === DUPLICATE_PHONE_ERROR_KEY && isCNFlyout && cnContactMode !== CN_CONTACT_MODES.EMAIL_ONLY
                ? authorModelData?.phoneAlreadyAssociatedInlineError
                : '';
            duplicatePhoneInlineError && (errorObject.phoneAlreadyAssociatedInlineError = duplicatePhoneInlineError);
            const verificationCodeInlineError =
              key === 'incorrectVerificationCode' ? authorModelData?.verificationCodeInlineMessage : '';
            verificationCodeInlineError && (errorObject.incorrectVerificationCode = verificationCodeInlineError);
            const expiredOtpInlineError =
              key === 'expiredOtpWarningMessage' ? authorModelData?.expiredOtpWarningInlineMessage : '';
            expiredOtpInlineError && (errorObject.expiredOtpWarningMessage = expiredOtpInlineError);
          });
          if (!isACDLenabled) {
            updateUserLoginDataLayer(error?.response?.headers?.['logineventdetails'] || '', false, {});
          }
          if (!isChangePwrd) {
            if (
              errorBody?.errorMessages &&
              errorBody?.errorMessages?.length &&
              !errorBody?.errorMessages?.includes('incorrectVerificationCode') &&
              !errorBody?.errorMessages?.includes(INVALID_EMAIL_ADDRESS)
            ) {
              setDisplayTypeOneError && setDisplayTypeOneError(true);
            } else {
              setDisplayTypeOneError && setDisplayTypeOneError(false);
              setTypeTwoErrorHeader && setTypeTwoErrorHeader(authorModelData?.errorMessage);
            }
            setPageError && setPageError(errorArray);
          } else {
            if (enablePhaseOne && errorBody?.errorMessages?.[0] === CHANGE_PASSWORD_TOKEN_EXPIRED_ERROR_KEY) {
              const modelRes = await fetchLinkJson(FORGOT_PASSWORD_KEY);
              if (modelRes) {
                setActiveKey(flyoutModalKeys?.ACTIVATE_OR_FORGOT_FORM);
                setTokenExpiredFlyout(true);
                setModelData({ ...modelData, [FORGOT_PASSWORD_KEY]: modelRes });
                setModelKey(FORGOT_PASSWORD_KEY);
              }
            } else if (errorBody?.errorMessages?.[0] && typeOneErrorKeys.includes(errorBody?.errorMessages?.[0])) {
              setDisplayTypeOneErrorInside(true);
            } else {
              setDisplayTypeOneErrorInside(false);
            }
            setErrorsList(errorArray);
          }
          setErrors(errorObject);
        }
      }
    } finally {
      setIsloading(false);
    }
  };

  const keepDisabledForChangePwrdFlow = () => {
    let isDisabled = false;
    if (
      isChangePwrd &&
      (isEAA || (!!maskedPhone?.length && (checkChinaLocale(currentLocale || '') || validateToken))) &&
      (!formData.verificationCode || !formData.password || !formData.confirmPwrd)
    )
      isDisabled = true;
    return isDisabled;
  };

  const generateOtpApi = async (phoneNumber?: string, phoneDecryptNeeded?: boolean): Promise<boolean> => {
    const phone = phoneNumber || formData?.mobilePhone || '';
    const duration = authorModelData?.timer;
    if (!isChangePwrd) {
      const secondsLeft = calculateOtpTimeLeft(phone, otpMapRef.current, duration);
      setTimeLeft(secondsLeft);

      if (secondsLeft > 0) {
        setIsOtpSent(true);
        setShowSendCode(false);
        return true;
      }
    }
    try {
      const resolvedPhoneNumber = phoneNumber
        ? phoneNumber
        : formData?.mobilePhone?.includes('+') || phoneDecryptNeeded // for change password, to fetch data from
          ? formData?.mobilePhone
          : `${selectedCountryCode ?? ''}${formData?.mobilePhone}`;
      const shouldEncryptPhone =
        isCNEaaPayloadEncrypt && !phoneNumber && (isMobileEditable || !phoneDetails?.phoneNumber);
      const encryptedPhoneNumber =
        shouldEncryptPhone && resolvedPhoneNumber ? encryptPassword(resolvedPhoneNumber, publicKey) : '';
      if (encryptedPhoneNumber) {
        setEncryptedPhone(encryptedPhoneNumber);
      }
      const payload = {
        phoneDecryptNeeded: phoneDecryptNeeded,
        phoneNumber: encryptedPhoneNumber || resolvedPhoneNumber,
        ...(encryptedPhoneNumber && { phoneEncryptedByClient: true }),
        ...(isChangePwrd && { noAccountAssociation: true }), // WCHAT-17488 - noAccountAssociation value is passed as true to BE, so that checkAssociation flag is sent as false to UXL.
        ...(maskedEmail.length && { mfaOption: constants.OTP_EMAIL, emailAddress: phoneNumber }),
      };
      const response = await axios.post(addSubDirectoryPrefixForNonEaa(generateOneTimePwrdUrl, isEAA), payload, {
        headers: {
          'Content-Type': 'application/json',
        },
        params: {
          ...(currentLocale && { locale: currentLocale }),
        },
      });
      setTransactionId(response.data);
      setIsOtpSent(true);
      setShowSendCode(false);
      setDisplayTypeOneError?.(false);
      setPageError?.([]);
      setErrors({});
      if (!isChangePwrd) {
        otpMapRef.current[phone] = Date.now();
        setTimeLeft(duration);
      }
      return true;
    } catch (error: unknown) {
      const genOtpError = error as ApiError;
      const errorResponse = genOtpError?.response?.data;
      const errorArray: string[] = [];
      const errorObject: FormError = {};
      const errorBody = errorResponse?.phoenixErrorMessages;
      errorBody?.errorMessages?.map((key: string) => {
        const errorString =
          key === DUPLICATE_PHONE_ERROR_KEY
            ? getAuthoredApiError(key) || getDuplicatePhonePageError()
            : getAuthoredApiError(key);
        const inlineError =
          key === DUPLICATE_PHONE_ERROR_KEY ? authorModelData['phoneAlreadyAssociatedInlineError'] : '';
        errorString && errorArray.push(errorString);
        inlineError && (errorObject['phoneAlreadyAssociatedInlineError'] = inlineError);
      });
      setDisplayTypeOneError && setDisplayTypeOneError(true);
      setPageError && setPageError(errorArray);
      setErrors(errorObject);
      setIsOtpSent(false);
      setShowSendCode(true);
      setTimeLeft(0);
      return false;
    } finally {
      setShowOTPSpinner(false);
    }
  };

  const handleOtp = async () => {
    if (isChangePwrd && communicationOption) {
      try {
        await generateOtpApi(communicationOption, true);
      } catch (error: any) {
        log.debug('Error fetching data:', error);
      } finally {
        setShowOTPSpinner(false);
      }
    } else {
      if (
        ((!isEAA && checkChinaLocale(currentLocale || '')) ||
          ((isEAA || checkChinaLocale(currentLocale || '')) && isChinesePhoneNumberValid())) &&
        !checkMobileNumValidity(formData?.mobilePhone)
      ) {
        const errObj: any = {};
        errObj.invalidMobilePhone = authorModelData?.invalidMobilePhone;
        pageLevelErrors.push(authorModelData?.invalidMobilePhoneMsg);
        pageLevelErrors.push(authorModelData?.invalidMobilePhone);
        scrollToClass();
        setPageError?.(pageLevelErrors);
        setErrors({ ...errors, ...errObj });
        setShowOTPSpinner(false);
      } else if (cnRequiresOtp) {
        if (cnContactMode === CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP) {
          generateOtpApi('', isCNEaaPayloadEncrypt);
        } else {
          generateOtpApi(cnOtpPhone, isCNEaaPayloadEncrypt);
        }
      } else {
        generateOtpApi('', isCNEaaPayloadEncrypt);
      }
    }
  };

  const handleTimerActiveChange = (timerState: boolean) => {
    setIsOtpSent(timerState);
  };

  const isValidChangePassword = passwordValid && !isFormDisabled;

  const isChinesePhoneNumberValid = () => {
    if (!userDetails?.givenName || !userDetails?.surName) {
      return false;
    }
    if (isMobileEditable) {
      return true;
    }
    const isUserInChina = userDetails.country?.toUpperCase() === constants.CHINA_COUNTRY_CODE;
    return (
      (phoneDetails?.isChinaMobileNumber ||
        (!phoneDetails?.phoneNumber && userDetails?.givenName && userDetails?.surName)) &&
      isUserInChina
    );
  };

  const getAuthoredApiError = (key: string) =>
    key === API_ERROR_KEY
      ? enablePhaseOne
        ? uxlFailureBannerText
        : pageContext?.uxlErrorMessage
      : authorModelData?.[key];
  const getDuplicatePhonePageError = () =>
    authorModelData?.[DUPLICATE_PHONE_ERROR_KEY] || authorModelData?.phoneAlreadyAssociatedInlineError;
  const getCreateOnlineAccountPhonePayload = (cnEaaPhoneNumber = '') => {
    const submittedPhoneNumber = transactionId
      ? (transactionId?.phoneNumber ?? formData?.mobilePhone?.includes('+'))
        ? formData?.mobilePhone
        : `${selectedCountryCode ?? ''}${formData?.mobilePhone}`
      : phoneDetails?.phoneNumber;

    if (!isCNFlyout) {
      return !isEAA && checkChinaLocale(currentLocale || '')
        ? {
            phoneNumber: transactionId
              ? formData?.mobilePhone?.replace(selectedCountryCode, '')
              : phoneDetails?.phoneNumber?.replace(selectedCountryCode, ''),
            phoneNumberCountryCode: selectedCountryCodeId,
          }
        : {
            phoneNumber: cnEaaPhoneNumber || submittedPhoneNumber,
            ...(encryptedPhone && { phoneEncryptedByClient: true }),
          };
    }

    if (!isEAA) {
      if (cnContactMode === CN_CONTACT_MODES.EMAIL_ONLY) {
        return {};
      }
      const sourcePhone = transactionId
        ? formData?.mobilePhone
        : cnContactMode === CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP
          ? phoneDetails?.phoneNumber
          : formData?.mobilePhone;
      if (!sourcePhone) {
        return {};
      }
      return {
        phoneNumber: sourcePhone.replace(selectedCountryCode || CHINESE_MOBILE_CODE, ''),
        phoneNumberCountryCode: selectedCountryCodeId,
      };
    }
    return {
      phoneNumber: cnEaaPhoneNumber || submittedPhoneNumber,
      ...(encryptedPhone && { phoneEncryptedByClient: true }),
    };
  };

  const handleCNContactContinue = async () => {
    const errObj: any = {};
    const errArr: string[] = [];
    let hasError = false;

    if (formData.email?.trim() && !isValidEmaiFormat(formData.email.trim())) {
      errObj.emailInvalidInlineError = authorModelData?.invalidEmailAddress;
      errArr.push(authorModelData?.invalidEmailAddress);
      hasError = true;
    }

    if (isCNContactStep && cnContactMode === CN_CONTACT_MODES.COLLECT_CN_PHONE_OTP) {
      const enteredMobileNumber = formData?.mobilePhone?.replace(selectedCountryCode || '', '').trim() || '';
      if (!enteredMobileNumber) {
        errObj.mobilePhoneRequired = authorModelData?.mobilePhoneRequired;
        errArr.push(authorModelData?.mobilePhoneRequired);
        hasError = true;
      } else if (selectedCountryCode !== CHINESE_MOBILE_CODE || !checkMobileNumValidity(formData?.mobilePhone)) {
        errObj.invalidMobilePhone = authorModelData?.invalidMobilePhone;
        errArr.push(authorModelData?.invalidMobilePhoneMsg);
        hasError = true;
      }
    } else if (
      !(cnStoredEmailDisabled ? !!phoneDetails?.emailAddress?.trim() : !!formData?.email?.trim()) &&
      !cnRequiresOtp
    ) {
      errObj.emailRequired = authorModelData?.emailRequired;
      errArr.push(authorModelData?.emailRequired);
      hasError = true;
    }

    if (hasError) {
      scrollToClass();
      setPageError?.(errArr.filter(Boolean));
      setErrors({ ...errors, ...errObj });
      setDisplayTypeOneError?.(false);
      setTypeTwoErrorHeader?.(authorModelData?.errorMessage);
      return;
    }

    setPageError?.([]);
    setErrors({});

    if (cnContactMode === CN_CONTACT_MODES.COLLECT_CN_PHONE_OTP) {
      // AC4: User entered phone in step 2 — backend hasn't sent OTP yet, send it now
      setIsloading(true);
      const isOtpGenerated = await generateOtpApi(cnOtpPhone, isCNEaaPayloadEncrypt);
      setIsloading(false);
      if (!isOtpGenerated) {
        scrollToClass();
        return;
      }
    } else if (cnContactMode === CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP) {
      // AC1/AC2: Backend already sent OTP at step 1 (activateAccount).
      // Preserve timer across back/forward navigation by reusing otpMapRef timestamp.
      const phone = phoneDetails?.phoneNumber || '';
      const duration = authorModelData?.timer;
      if (phone && !otpMapRef.current[phone]) {
        otpMapRef.current[phone] = Date.now();
      }
      const secondsLeft = phone ? calculateOtpTimeLeft(phone, otpMapRef.current, duration) : duration;
      setIsOtpSent(true);
      setShowSendCode(false);
      setTimeLeft(secondsLeft > 0 ? secondsLeft : 0);
    }

    setCnFlyoutStep(CN_FLYOUT_STEPS.SET_PASSWORD);
  };

  const handleBackToCNContactInfo = () => {
    setFormData(prevData => ({
      ...prevData,
      password: '',
      confirmPwrd: '',
      verificationCode: '',
    }));
    setPasswordConditions(authorModelData?.pwrdHeader ? passwordCondLabelArr : passwordCondArr);
    setPasswordValid(false);
    setCnFlyoutStep(CN_FLYOUT_STEPS.CONTACT_INFO);
    setPageError?.([]);
    setErrors({});
    setDisplayTypeOneError?.(false);
    setTypeTwoErrorHeader?.('');
  };

  const hasPhoneNumber =
    (isEAA || checkChinaLocale(currentLocale || '')) &&
    !isMobileEditable &&
    phoneDetails?.maskedNumber &&
    selectedCountryCode === CHINESE_MOBILE_CODE &&
    isChinesePhoneNumberValid();
  const shouldRenderPhoneOtpFields =
    !isChangePwrd &&
    !isCNPasswordStep &&
    (isCNContactStep
      ? cnContactMode !== CN_CONTACT_MODES.EMAIL_ONLY
      : !isCNFlyout &&
        ((isMobileAuthFeatureEnabled && (!userDetails?.givenName || !userDetails?.surName)) ||
          ((isEAA || checkChinaLocale(currentLocale || '')) && isChinesePhoneNumberValid()) ||
          (!isEAA && checkChinaLocale(currentLocale || '') && isFormDisabled)));
  const shouldShowEmailField =
    !isChangePwrd && (!isEAA || (isEAA && !phoneDetails?.hideEmail)) && !isCNContactStep && !isCNFlyout;

  const isMobile = canUseDOM && window.innerWidth < 768;

  return (
    <StyledSetPassword data-component-name="m-account-SetPassword" data-testid="account-SetPassword">
      <div
        className={clsx(
          'set-password-container',
          !isChangePwrd && 'offset-lg-1',
          isFormDisabled && 'form-disabled',
          enablePhaseOne && isChangePwrd ? 'pt-4' : 'pt-5 pt-lg-0',
          enablePhaseOne && !isChangePwrd && 'm-0',
          isChangePwrd && !isFlyout && 'pt-5 pt-lg-5'
        )}
      >
        {isChangePwrd ? (
          <>
            {checkChinaLocale(currentLocale || '') ||
            resetToken ||
            validateToken ||
            storeValidateToken ||
            enablePhaseThree ? (
              <>
                {!!maskedEmail?.length && !isEAA ? (
                  <Heading
                    variation={isFlyout ? Types.headingType.subtitle : Types.headingType.title}
                    titleText={authorModelData?.forgotPasswordLabel}
                    customClass={'mb-5'}
                    fontSize={isFlyout ? Types.size.extraLarge : Types.size.small}
                  />
                ) : enablePhaseOne ? (
                  <Heading
                    variation={Types.headingType.subtitle}
                    titleText={authorModelData?.changePwrdTitle}
                    customClass={'mb-5'}
                    fontSize={Types.size.extraLarge}
                  />
                ) : (
                  <Heading
                    variation={Types.headingType.title}
                    titleText={authorModelData?.changePwrdTitle}
                    customClass={'my-5'}
                    fontSize={isDesktopAndAbove ? Types.size.medium : Types.size.small}
                  />
                )}
                {enablePhaseThree && (
                  <>
                    <hr className="flyout-divider" />
                    <div className="t-overline-alt-normal mt-4 mb-2">
                      {stepCounter?.replace('{0}', '2').replace('{1}', '2')}
                    </div>
                  </>
                )}
                {!!maskedEmail?.length && !isEAA && (
                  <div className={clsx("mb-2", isFlyout ? "t-subtitle-m" : "t-subtitle-xl")}>{authorModelData?.confirmIdAndChangePwrdLabel}</div>
                )}
                {!!maskedPhone?.length && (
                  <RichText
                    text={authorModelData?.subTitle?.replace('{0}', maskedPhone)}
                    customClass={'mb-4 t-font-m'}
                    componentId="reset-pwrd-subtitle"
                  />
                )}
                {!!maskedEmail?.length && (
                  <RichText
                    text={authorModelData?.codeViaEmailLabel?.replace('{0}', maskedEmail)}
                    customClass={'mb-4 t-font-m'}
                    componentId="reset-pwrd-subtitle"
                  />
                )}
              </>
            ) : enablePhaseOne ? (
              <Heading
                variation={Types.headingType.subtitle}
                titleText={authorModelData?.changePwrdTitle}
                customClass={'mb-5'}
                fontSize={Types.size.extraLarge}
              />
            ) : (
              <Heading
                variation={Types.headingType.title}
                titleText={authorModelData?.changePwrdTitle}
                customClass={'my-4'}
                fontSize={Types.size.small}
              />
            )}
            {errorsList?.length ? (
              <Messages messageType="error-sev1" className="mt-3 account-page-error-msg mb-4 px-2">
                {!!errorsList?.length &&
                  (displayTypeOneErrorInside ? (
                    <RichText text={errorsList?.[0]} componentId="password-error-message" />
                  ) : (
                    <>
                      <span>{authorModelData?.errorMessage}</span>
                      {errorsList.map(error => (
                        <ul>
                          <li>{error}</li>
                        </ul>
                      ))}
                    </>
                  ))}
              </Messages>
            ) : null}
          </>
        ) : (
          !enablePhaseOne &&
          !isCNFlyout && (
            <RichText
              customClass="set-password__heading t-subtitle-xl mb-4"
              text={authorModelData?.setYourPwrd}
              componentId="set-your-password"
            />
          )
        )}
        {isCNPasswordStep && cnRequiresOtp && !showSendCode && (
          <RichText
            text={authorModelData?.otpSentMessage?.replace(
              '{0}',
              transactionId?.maskedNumber ||
                phoneDetails?.maskedNumber ||
                getMaskedCnPhoneDisplay(formData?.mobilePhone || '', selectedCountryCode || CHINESE_MOBILE_CODE),
            )}
            customClass="mb-4 t-body-m"
            componentId="otp-sent-subtitle"
          />
        )}
        <div className="set-password-container__form">
          <div className="set-password-container__form--layout row">
            <div className="col-12 col-md-12">
              {isChangePwrd &&
                (!!maskedPhone?.length || !!maskedEmail.length) &&
                (checkChinaLocale(currentLocale || '') || validateToken || storeValidateToken || enablePhaseThree) && (
                  <StyledInputTextFieldNumber
                    className={` mb-2 pb-2 mt-3
                    ${
                      errors.verificationCodeError || errors?.expiredOtpWarningMessage
                        ? 'is-error'
                        : formData.verificationCode
                          ? 'is-active'
                          : ''
                    } ${inlineLabel ? 'm-input-text-field' : 'm-input-field'}
                  `}
                  >
                    <InputTextField
                      iconClass="icon icon-clear"
                      // showIcon={otpError.otp !== undefined}
                      type="number"
                      label={authorModelData?.verificationCodeLabel}
                      inputValue={formData.verificationCode}
                      showErrorMessage={
                        !!errors?.expiredOtpWarningMessage ||
                        errors?.incorrectVerificationCode ||
                        errors?.expiredTransactionCode ||
                        errors?.verificationCodeError
                          ? true
                          : false
                      }
                      messageToShow=""
                      setErrorHtml={true}
                      messageClass="error-label"
                      className={clsx(
                        inlineLabel ? 'm-input-text-field' : 'm-input-field',
                        formData.verificationCode !== undefined &&
                          formData.verificationCode !== '' &&
                          errors.verificationCodeError === undefined &&
                          errors?.expiredOtpWarningMessage === undefined
                          ? 'is-active'
                          : '',
                      )}
                      getInputValue={(val: string) => {
                        setFormData({ ...formData, verificationCode: val });
                      }}
                      testId="verificationCode"
                    />

                    <div
                      className={clsx(
                        't-font-xs d-flex mt-1',
                        isFormDisabled && 'disabled-text',
                        (errors?.expiredOtpWarningMessage ||
                          errors?.incorrectVerificationCode ||
                          errors?.expiredTransactionCode) &&
                          'validation-msg',
                      )}
                    >
                      {!!maskedEmail.length && (
                        <div className="t-font-xs pb-2 d-flex resend-code-container">
                          <RichText text={authorModelData?.resendCode} componentId="resend-code-label" />
                          <div
                            onClick={() => {
                              setShowOTPSpinner(true);
                              handleOtp();
                            }}
                            // data-custom_click_track_value={`${ENTER_YOUR_CODE}|${RESEND_CODE}|${INTERNAL}|`}
                          >
                            <RichText
                              text={authorModelData?.resendANewCode}
                              componentId="resend-code-button"
                              customClass="ml-1 help-text-decoration"
                            />
                          </div>
                          {showOTPSpinner && <div className="m-spinner-s"></div>}
                        </div>
                      )}
                      {!!maskedPhone?.length &&
                        (authorModelData?.timer &&
                        (enablePhaseOne ? !!storeValidateToken : pageContext?.enableTimer === 'true') ? (
                          <OtpTimer
                            handleOtp={handleOtp}
                            onTimerActiveChange={handleTimerActiveChange}
                            isOtpSent={isOtpSent}
                            timer={authorModelData?.timer}
                            timerRunningLabel={authorModelData?.timerRunningLabel}
                            resendNewCodeLabel={authorModelData?.resendANewCode}
                            otpValidationError={
                              errors?.incorrectVerificationCode ||
                              errors?.expiredTransactionCode ||
                              errors?.expiredOtpWarningMessage
                                ? true
                                : false
                            }
                            incorrectCodeRunningLabel={authorModelData?.incorrectCodeRunningLabel}
                            verificationCodeInlineMessage={
                              errors?.expiredOtpWarningMessage
                                ? authorModelData?.expiredOtpWarningInlineMessage
                                : authorModelData?.verificationCodeInlineMessage
                            }
                            didNotGetACodeLabel={authorModelData?.didNotGetACode}
                          />
                        ) : (
                          <>
                            <RichText
                              text={
                                errors?.expiredOtpWarningMessage
                                  ? authorModelData?.expiredOtpWarningInlineMessage
                                  : authorModelData?.didNotGetACode
                              }
                              componentId="verification-code"
                            />

                            <div
                              onClick={handleOtp}
                              className="ml-1 pointer-cursor help-text-decoration mr-3"
                              data-testId="verify_code"
                            >
                              <RichText text={authorModelData?.resendANewCode} componentId="resend-new-code" />
                            </div>
                          </>
                        ))}
                    </div>
                    {errors.verificationCodeError && <span>{errors.verificationCodeError}</span>}
                  </StyledInputTextFieldNumber>
                )}
              {isCNContactStep && !isChangePwrd && (!isEAA || (isEAA && !phoneDetails?.hideEmail)) && (
                <div
                  className={clsx(
                    enablePhaseOne ? 'pb-4' : 'email-field mb-5 pb-2',
                    cnStoredEmailDisabled
                      ? 'is-disabled'
                      : errors.emailRequired || errors.emailInvalidInlineError
                        ? 'is-error'
                        : formData.email
                          ? 'is-active'
                          : '',
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                  )}
                >
                  <InputTextField
                    infoLabel="email"
                    label={
                      cnContactMode !== CN_CONTACT_MODES.EMAIL_ONLY
                        ? authorModelData?.emailOptionalTitle
                        : authorModelData?.email
                    }
                    ref={emailInputRef}
                    testId="email"
                    inputValue={formData.email}
                    inputMaxLength={80}
                    disabled={isFormDisabled || cnStoredEmailDisabled}
                    className={inlineLabel ? 'm-input-text-field' : 'm-input-field'}
                    getInputValue={(val: string) => {
                      val && resetForm('email');
                      setFormData(prevData => ({ ...prevData, email: val }));
                    }}
                    getInputProps={() => ({
                      autoComplete: 'off',
                      id: 'email',
                      type: 'text',
                    })}
                    getLabelProps={() => ({
                      htmlFor: 'email',
                      className: 't-body-s',
                    })}
                    messageToShow={errors.emailRequired || errors.emailWarning || errors?.emailInvalidInlineError || ''}
                    showErrorMessage={
                      !!errors.emailRequired || !!errors.emailWarning || !!errors?.emailInvalidInlineError
                    }
                  />
                </div>
              )}
              {shouldRenderPhoneOtpFields && (
                <>
                  <div
                    className={clsx(
                      'mb-5 pb-2',
                      isFormDisabled && !isCNContactStep ? 'is-disabled' : formData.mobilePhone ? 'is-active' : '',
                      inlineLabel ? 'm-input-text-field' : 'm-input-field',
                      isCNContactStep && 'cn-phone-stacked',
                      isCNContactStep &&
                        cnContactMode !== CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP &&
                        'cn-phone-editable',
                    )}
                    ref={phoneInputRef}
                  >
                    <PhoneNumberField
                      label={authorModelData?.countryCodeLabel ?? ''}
                      inputTextLabel={
                        isCNContactStep
                          ? authorModelData?.mobilePhone
                          : checkChinaLocale(currentLocale || '') ||
                              (selectedCountryCode === CHINESE_MOBILE_CODE && isEAA && isChinesePhoneNumberValid())
                            ? authorModelData?.mobilePhone
                            : authorModelData?.mobilePhoneOptional
                      }
                      defaultCCOption={defaultContryCode}
                      dropdownCCOptions={authorModelData?.countriesDialCodes}
                      mobileInputVal={
                        isCNContactStep
                          ? phoneDetails?.maskedNumber?.replace(countryDialCode || '', '') ||
                            (cnContactMode === CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP
                              ? getMaskedCnPhoneDisplay(
                                  phoneDetails?.phoneNumber || '',
                                  selectedCountryCode || CHINESE_MOBILE_CODE,
                                )
                              : formData?.mobilePhone?.replace(selectedCountryCode || '', '').trim() || '')
                          : !isMobileEditable
                            ? phoneDetails?.maskedNumber?.replace(countryDialCode || '', '') || ''
                            : ''
                      }
                      isInputError={
                        !!errors?.mobilePhoneRequired ||
                        !!errors?.invalidMobilePhone ||
                        !!errors?.invalidMobilePhoneMsg ||
                        !!errors?.phoneAlreadyAssociatedInlineError
                      }
                      inputErrorMsg={''}
                      disabled={
                        (isFormDisabled && !isCNContactStep) ||
                        (!isMobileEditable &&
                          !!phoneDetails?.phoneNumber &&
                          !phoneDetails?.phoneNumber?.includes(NON_SUPPORTED_PHONE_NUMBER) &&
                          !isCNContactStep) ||
                        (isCNContactStep && cnContactMode === CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP)
                      }
                      fetchMobileNumber={(obj: any) => {
                        fetchMobileNumber(obj);
                      }}
                      isNonChinaMobileEnabled={isNonChinaMobileEnabled}
                      isRegisteredNumber={
                        (isCNContactStep && cnContactMode === CN_CONTACT_MODES.EXISTING_CN_PHONE_OTP) ||
                        (!!phoneDetails?.phoneNumber &&
                        !phoneDetails?.phoneNumber?.includes(NON_SUPPORTED_PHONE_NUMBER) &&
                        !isCNContactStep
                          ? !isMobileEditable
                          : false)
                      }
                      isMobileEditable={
                        isCNContactStep ? cnContactMode === CN_CONTACT_MODES.COLLECT_CN_PHONE_OTP : isMobileEditable
                      }
                      dropdownId={'dropdown-label-fp-country-dial-code'}
                      inlineLabel={inlineLabel}
                    />
                    <div className="d-md-flex d-block">
                      {isMobile && (
                        <>
                          {errors.mobilePhoneRequired && (
                            <span className="validation-msg t-font-xs">{errors.mobilePhoneRequired}</span>
                          )}
                          {errors?.mobilePhoneWarning && (
                            <span className="validation-msg t-font-xs">{errors?.mobilePhoneWarning}</span>
                          )}
                          {errors?.phoneAlreadyAssociatedInlineError && (
                            <span className="validation-msg t-font-xs">
                              {errors?.phoneAlreadyAssociatedInlineError}
                            </span>
                          )}
                        </>
                      )}
                      {!isCNContactStep && (!phoneDetails?.phoneNumber || isMobileEditable) && showSendCode && (
                        <div
                          className={clsx(
                            't-font-xs d-flex phoen-help-text help-text-decoration mt-1 pr-2',
                            isFormDisabled && 'disabled-text',
                          )}
                          onClick={handleOtp}
                          data-testId="verify_code"
                        >
                          {authorModelData?.sendVerificationCode}
                        </div>
                      )}
                      {!isMobile && (
                        <>
                          {errors.mobilePhoneRequired && (
                            <span className="validation-msg t-font-xs ml-2 pr-5">{errors.mobilePhoneRequired}</span>
                          )}
                          {errors?.mobilePhoneWarning && (
                            <span className="validation-msg t-font-xs ml-2 pr-5">{errors?.mobilePhoneWarning}</span>
                          )}
                          {errors?.phoneAlreadyAssociatedInlineError && (
                            <span className="validation-msg t-font-xs ml-2 pr-5">
                              {errors?.phoneAlreadyAssociatedInlineError}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}
              {((shouldRenderPhoneOtpFields && !isCNContactStep) || (isCNPasswordStep && cnRequiresOtp)) && (
                <StyledInputTextFieldNumber
                  className={`mb-4 pb-2
                    ${
                      isFormDisabled && !isCNFlyout
                        ? 'is-disabled'
                        : errors.verificationCodeError ||
                            errors?.incorrectVerificationCode ||
                            errors?.expiredTransactionCode ||
                            errors?.expiredOtpWarningMessage
                          ? 'is-error'
                          : formData.verificationCode
                            ? 'is-active'
                            : ''
                    } ${inlineLabel ? 'm-input-text-field' : 'm-input-field'}
                  `}
                  ref={otpInputRef}
                >
                  <InputTextField
                    iconClass="icon icon-clear"
                    // showIcon={otpError.otp !== undefined}
                    type="text"
                    label={authorModelData?.verificationCodeLabel}
                    inputValue={formData.verificationCode}
                    showErrorMessage={
                      errors?.incorrectVerificationCode ||
                      errors?.expiredTransactionCode ||
                      errors?.verificationCodeError ||
                      errors?.expiredOtpWarningMessage
                        ? true
                        : false
                    }
                    messageToShow={''}
                    setErrorHtml={true}
                    messageClass="error-label"
                    className={clsx(
                      inlineLabel ? 'm-input-text-field' : 'm-input-field',
                      formData.verificationCode !== undefined &&
                        formData.verificationCode !== '' &&
                        errors.verificationCodeError === undefined
                        ? 'is-active'
                        : ''
                    )}
                    getInputValue={(val: string) => {
                      setFormData({ ...formData, verificationCode: val });
                      setErrors({ ...errors, verificationCodeError: undefined });
                    }}
                    testId="otp-verificationCode"
                  />
                  {!showSendCode && (
                    <div
                      className={clsx(
                        't-font-xs d-flex mt-1',
                        isFormDisabled && 'disabled-text',
                        (errors?.incorrectVerificationCode ||
                          errors?.expiredTransactionCode ||
                          errors?.expiredOtpWarningMessage) &&
                          'validation-msg'
                      )}
                    >
                      {(
                        isCNPasswordStep && cnRequiresOtp
                          ? authorModelData?.timer
                          : pageContext?.enableTimer === 'true' && authorModelData?.timer
                      ) ? (
                        <OtpTimer
                          handleOtp={handleOtp}
                          onTimerActiveChange={handleTimerActiveChange}
                          isOtpSent={isOtpSent}
                          timer={timeLeft}
                          timerRunningLabel={authorModelData?.timerRunningLabel}
                          resendNewCodeLabel={authorModelData?.resendANewCode}
                          otpValidationError={
                            errors?.incorrectVerificationCode ||
                            errors?.expiredTransactionCode ||
                            errors?.expiredOtpWarningMessage
                              ? true
                              : false
                          }
                          incorrectCodeRunningLabel={authorModelData?.incorrectCodeRunningLabel}
                          verificationCodeInlineMessage={
                            errors?.incorrectVerificationCode || errors?.expiredTransactionCode
                              ? authorModelData?.verificationCodeInlineMessage
                              : errors?.expiredOtpWarningMessage
                                ? authorModelData?.expiredOtpWarningInlineMessage
                                : ''
                          }
                          didNotGetACodeLabel={authorModelData?.didNotGetACode}
                        />
                      ) : (
                        <>
                          <RichText
                            text={
                              errors?.incorrectVerificationCode || errors?.expiredTransactionCode
                                ? authorModelData?.verificationCodeInlineMessage
                                : errors?.expiredOtpWarningMessage
                                  ? authorModelData?.expiredOtpWarningInlineMessage
                                  : authorModelData?.didNotGetACode
                            }
                            componentId="verification-code"
                          />
                          <div
                            onClick={handleOtp}
                            className="ml-1 pointer-cursor help-text-decoration mr-3"
                            data-testid="verify_code"
                          >
                            <RichText text={authorModelData?.resendANewCode} componentId="resend-new-code" />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {errors.verificationCodeError && <span>{errors.verificationCodeError}</span>}
                </StyledInputTextFieldNumber>
              )}
              {shouldShowEmailField && (
                <div
                  className={clsx(
                    enablePhaseOne ? 'pb-4' : 'email-field mb-5 pb-2',
                    isFormDisabled ||
                      (isEAA || !checkChinaLocale(currentLocale || '')
                        ? !!phoneDetails?.emailAddress
                        : !phoneDetails?.maskedNumber &&
                          !phoneDetails?.isChinaMobileNumber &&
                          !!phoneDetails?.emailAddress)
                      ? 'is-disabled'
                      : errors.emailRequired || errors.emailInvalidInlineError
                        ? 'is-error'
                        : formData.email
                          ? 'is-active'
                          : '',
                    inlineLabel ? 'm-input-text-field' : 'm-input-field',
                  )}
                >
                  <InputTextField
                    infoLabel="email"
                    label={
                      checkChinaLocale(currentLocale || '')
                        ? shouldRenderPhoneOtpFields
                          ? authorModelData?.emailOptionalTitle
                          : authorModelData?.email
                        : isCoutriesSame(constants?.CHINA_COUNTRY_CODE, userDetails?.country) &&
                            isChinesePhoneNumberValid() &&
                            isEAA
                          ? authorModelData?.emailOptionalTitle
                          : authorModelData?.email
                    }
                    ref={emailInputRef}
                    testId="email"
                    inputValue={formData.email}
                    inputMaxLength={80}
                    disabled={
                      isFormDisabled ||
                      (isEAA || !checkChinaLocale(currentLocale || '')
                        ? !!phoneDetails?.emailAddress
                        : !phoneDetails?.maskedNumber &&
                          !phoneDetails?.isChinaMobileNumber &&
                          !!phoneDetails?.emailAddress)
                    }
                    className={inlineLabel ? 'm-input-text-field' : 'm-input-field'}
                    getInputValue={(val: string) => {
                      val && resetForm('email');
                      setFormData(prevData => ({ ...prevData, email: val }));
                    }}
                    getInputProps={() => ({
                      autoComplete: 'off',
                      id: 'email',
                      type: 'text',
                    })}
                    getLabelProps={() => ({
                      htmlFor: 'email',
                      className: 't-body-s',
                    })}
                    messageToShow={errors.emailRequired || errors.emailWarning || errors?.emailInvalidInlineError || ''}
                    showErrorMessage={
                      !!errors.emailRequired || !!errors.emailWarning || !!errors?.emailInvalidInlineError
                    }
                  />
                </div>
              )}
              {!isCNContactStep && (
                <>
                  <PasswordInput
                    value={formData.password}
                    id="newpassword"
                    name="password"
                    disable={isFormDisabled}
                    hasError={!!errors.newPwrdRequired}
                    label={isChangePwrd ? authorModelData?.newPwrdLabel : authorModelData?.pwrdLabel}
                    passwordType={passwordType}
                    error={errors?.newPwrdRequired}
                    customClass={clsx(enablePhaseOne || (isChangePwrd && !isFlyout) ? 'mb-4' : 'mb-2')}
                    iconClass={passwordType === 'password' ? 'icon-visibility-off' : 'icon-visibility'}
                    labelClass="t-body-s"
                    showValidations={true}
                    passwordConditions={passwordConditions}
                    handleChange={handleChange}
                    onIconClick={() => setPasswordType(passwordType === 'password' ? 'text' : 'password')}
                    isValid={passwordValid}
                    showCheckIcon={true}
                    ariaLabel="password"
                    innerLabel={inlineLabel}
                    checkHeaderLabel={authorModelData?.pwrdHeader}
                  />
                  <PasswordInput
                    value={formData.confirmPwrd}
                    id="confirmPassword"
                    name="confirmPwrd"
                    disable={!isValidChangePassword}
                    hasError={!!errors.confirmPwrdAndPwrdMatch}
                    label={authorModelData?.confirmPwrdLabel}
                    passwordType={confirmPasswordType}
                    error={errors?.confirmPwrdAndPwrdMatch}
                    customClass="mb-5"
                    iconClass={confirmPasswordType === 'password' ? 'icon-visibility-off' : 'icon-visibility'}
                    labelClass="t-body-s"
                    handleChange={handleChange}
                    onIconClick={() => setConfirmPasswordType(confirmPasswordType === 'password' ? 'text' : 'password')}
                    isValid={formData.confirmPwrd?.length && formData.confirmPwrd === formData.password ? true : false}
                    showCheckIcon={true}
                    innerLabel={inlineLabel}
                    errorLabelClass={inlineLabel ? 'error-label' : ''}
                  />
                </>
              )}
            </div>
          </div>
          <div className="col-12 px-0">
            {isCNContactStep ? (
              <Button
                type={Types.ButtonTypeVariation.Button}
                className="m-button-m m-button-primary px-4"
                callback={handleCNContactContinue}
                isDisabled={isLoading}
                testId="cn-contact-continue-btn"
                ariaLabel={authorModelData?.continueButtonLabel || ''}
              >
                <div className={clsx(!isLoading ? 'd-none' : 'm-spinner-s')}></div>
                <div className={clsx(isLoading ? 'd-none' : '')}>{authorModelData?.continueButtonLabel || ''}</div>
              </Button>
            ) : (
              <Button
                type={Types.ButtonTypeVariation.Button}
                buttonCopy={''}
                className={clsx(
                  'm-button-m m-button-primary submit-btn',
                  (isFormDisabled ||
                    keepDisabledForChangePwrdFlow() ||
                    (formData.confirmPwrd?.length && formData.password?.length ? false : true)) &&
                    'disabled',
                  enablePhaseOne ? 'px-4 create-account-submit-btn-flyout' : 'px-5 create-account-submit-btn',
                )}
                isLink={false}
                custom_click_track_value={
                  isEAA
                    ? `${customClickTrackingPageName}|${
                        isChangePwrd ? CHANGE_PASSWORD_SUBMIT : ACTIVATE_ACCOUNT_RHS_SUBMIT
                      }|${INTERNAL}|`
                    : isFlyout
                      ? submitCustomClickTrackValue
                      : ''
                }
                isDisabled={
                  isFormDisabled ||
                  isLoading ||
                  keepDisabledForChangePwrdFlow() ||
                  (formData.confirmPwrd?.length && formData.password?.length ? false : true)
                }
                callback={handleSubmit}
                testId="submit_btn"
                ariaLabel={authorModelData?.submitButtonLabel}
              >
                <div className={clsx(!isLoading ? 'd-none' : 'm-spinner-s')} data-testid="set-password-spinner"></div>
                <div className={clsx(isLoading ? 'd-none' : '')}>{authorModelData?.submitButtonLabel}</div>
              </Button>
            )}
            {isCNPasswordStep && (
              <div className="d-flex align-items-center mt-4">
                <span
                  className="m-link-back-tertiary-button pointer-cursor t-font-s"
                  onClick={handleBackToCNContactInfo}
                  data-testid="back-to-contact-info"
                >
                  {authorModelData?.backToContactInfoCTALabel}
                </span>
              </div>
            )}
            {!enablePhaseOne && isChangePwrd && (
              //using 'a' tag as 'm-link-action' button font doesn't match with invision.
              <span className="d-block d-sm-inline mt-4 mt-sm-0 text-center">
                <a
                  data-skip-flyout="true"
                  href={cancelButtonLink}
                  className={clsx('cancel-button m-sm-5 m-link p-0', isChangePwrd ? 't-font-s' : 't-font-m')}
                  data-testid={authorModelData?.cancelCtaLabel}
                >
                  {authorModelData?.cancelCtaLabel}
                </a>
              </span>
            )}
          </div>
          {!enablePhaseOne && (
            <Text
              customClass={'t-font-xs pt-2 mt-5 d-lg-none'}
              copyText={authorModelData?.copyright?.replace('{0}', String(moment().year()))}
              fontSize={Types.size.extraSmall}
              element={Types.tags.paragraph}
            />
          )}
        </div>
      </div>
      {enablePhaseOne && <FlyOutModalStyle />}
    </StyledSetPassword>
  );
};

export const SetPasswordConfig = {
  emptyLabel: 'SetPassword',
  isEmpty: false,
  resourceType: `mi-aem-account/components/content/setpassword`,
};
export const SetPasswordEditable = (props: any) => {
  return (
    <EditableComponent config={SetPasswordConfig} {...props}>
      <SetPassword {...props} />
    </EditableComponent>
  );
};
